import { BlogArticleTemplate } from './BlogArticleTemplate';

export function AIAnxiety() {
  const metadata = {
    category: 'Mental Control & Overthinking',
    categorySlug: 'mental-control-overthinking',
    title: 'AI Anxiety: Why You Can\'t Stop Thinking About the Future (And What to Do About It)',
    author: 'Mental Control Team',
    publishedDate: '2024-01-15',
    readingTime: '12 min',
    featuredImage: {
      url: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&h=675&fit=crop',
      alt: 'AI and future technology concept'
    }
  };

  const faqItems = [
    {
      question: "Isn't it irresponsible to not constantly monitor AI developments?",
      answer: "There's a difference between informed awareness and anxiety-driven obsession. Weekly structured reviews keep you informed without mental health cost."
    },
    {
      question: "What if I'm genuinely in a high-risk field for automation?",
      answer: "Then take action now. The techniques above still apply—they just emphasize action over analysis. Develop adjacent skills, explore pivots, build unique value. But do it with structure, not panic."
    },
    {
      question: "How do I know if I'm preparing enough?",
      answer: "Ask: 'Am I taking regular action based on this thinking?' If yes, you're preparing. If you're only analyzing without doing, you're overthinking."
    }
  ];

  const relatedArticles = [
    {
      title: 'Decision Fatigue: Why You Can\'t Make Decisions by 3 PM',
      excerpt: 'Your willpower isn\'t weak—your decision protocol is missing. Learn the decision budget system.',
      thumbnail: 'https://images.unsplash.com/photo-1494368308039-ed3393a402a4?w=600&h=400&fit=crop',
      slug: 'decision-fatigue',
      category: 'Mental Control'
    },
    {
      title: 'How to Think Without Getting Stuck',
      excerpt: 'Productive thinking has clear start and stop points. Mental containment systems let you think deeply without looping endlessly.',
      thumbnail: 'https://images.unsplash.com/photo-1516302752625-fcc3c50ae61f?w=600&h=400&fit=crop',
      slug: 'mental-containment-system',
      category: 'Structured Thinking'
    },
    {
      title: 'The Hidden Cost of Mental Loops at Work',
      excerpt: 'Repeated analysis reduces output quality and decision speed. Learn loop interruption techniques that preserve performance under pressure.',
      thumbnail: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=400&fit=crop',
      slug: 'mental-loops-at-work',
      category: 'Productivity'
    }
  ];

  return (
    <BlogArticleTemplate 
      metadata={metadata} 
      faqItems={faqItems}
      relatedArticles={relatedArticles}
    >
      <p className="lead text-xl text-gray-700 mb-8">
        Future uncertainty about AI triggers endless analysis with no resolution. Learn structured forecasting techniques that contain projection anxiety without suppressing it.
      </p>

      <p className="mb-6">
        <strong>It's 3 AM and you're calculating how many years until AI takes your job.</strong>
      </p>

      <p className="mb-6">
        Or you're mapping out a career pivot strategy for skills that might be obsolete by 2027. Or you're doom-scrolling through AI news, each article spawning seventeen new scenarios about what the future might look like.
      </p>

      <p className="mb-6">
        Welcome to AI anxiety—the newest form of future-oriented overthinking that's keeping high-performers up at night.
      </p>

      <p className="mb-6">
        Unlike past technological shifts, AI advancement feels different. It's faster, more uncertain, and impossible to ignore. Your brain knows something significant is happening, but doesn't know <em>what</em> to prepare for.
      </p>

      <p className="mb-6">
        So it just... keeps analyzing. Endlessly.
      </p>

      <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Why AI Triggers Unique Anxiety Patterns</h2>

      <p className="mb-6">
        "AI anxiety is different from general future anxiety," explains Dr. Marcus Kim, who researches technology-related stress. "With previous disruptions, you could see the shape of the change coming. With AI, the endpoint is fundamentally unknowable, which creates analysis without possible resolution."
      </p>

      <p className="mb-6">
        Your brain is designed to predict threats and create response plans. But AI development is:
      </p>

      <ul className="list-disc pl-6 mb-6 space-y-2">
        <li><strong>Exponentially accelerating</strong> - The pace keeps changing</li>
        <li><strong>Fundamentally uncertain</strong> - Experts disagree on timeline and impact</li>
        <li><strong>Personally relevant</strong> - Could affect your career, relationships, identity</li>
        <li><strong>Constantly in the news</strong> - New developments every week</li>
      </ul>

      <p className="mb-6">
        This combination creates what psychologists call "open-loop anxiety"—your brain keeps trying to solve a problem that has no current solution.
      </p>

      <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">The Future Projection Trap</h2>

      <p className="mb-6">
        Here's what happens:
      </p>

      <p className="mb-6">
        You read about a new AI capability. Your brain immediately projects forward: "If AI can do X now, it will do Y in six months, which means Z by next year, which means my skills become irrelevant by 2026, which means I should start learning [new skill], but what if that becomes obsolete too, and maybe I should change careers entirely, but to what, and what if I'm too late already, and..."
      </p>

      <p className="mb-6">
        Infinite loop. No resolution. Maximum anxiety.
      </p>

      <p className="mb-6">
        The problem isn't that you're thinking about the future. The problem is that you're trying to analyze infinite possibilities simultaneously without structure.
      </p>

      <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Structured Forecasting: Containing Projection Without Suppressing It</h2>

      <p className="mb-6">
        Here's how to work with your brain's future-orientation instead of fighting it:
      </p>

      <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. The Scenario Boundaries Method</h3>

      <p className="mb-6">
        Your brain wants to explore possibilities. Let it—but with limits.
      </p>

      <p className="mb-4"><strong>The practice:</strong></p>
      <ul className="list-disc pl-6 mb-6 space-y-2">
        <li>Set a 20-minute timer</li>
        <li>Write down exactly three future scenarios: optimistic, realistic, pessimistic</li>
        <li>For each scenario, identify one specific preparation action</li>
        <li>Take one preparation action this week</li>
        <li>Close the analysis session</li>
      </ul>

      <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. The Weekly Future Window</h3>

      <p className="mb-6">
        Instead of thinking about AI implications constantly, schedule it.
      </p>

      <p className="mb-4"><strong>Implementation:</strong></p>
      <ul className="list-disc pl-6 mb-6 space-y-2">
        <li>Pick one 30-minute slot per week</li>
        <li>During that time only, engage with AI news and analysis</li>
        <li>Make notes on any action items</li>
        <li>For the rest of the week, redirect AI thoughts to your scheduled window</li>
      </ul>

      <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. The Action-Oriented Threshold</h3>

      <p className="mb-6">
        For every hour you spend thinking about AI futures, spend two hours taking preparedness action.
      </p>

      <p className="mb-4"><strong>Examples of action:</strong></p>
      <ul className="list-disc pl-6 mb-6 space-y-2">
        <li>Learn a new tool (not just research it)</li>
        <li>Build something using AI</li>
        <li>Have conversations with people in different fields</li>
        <li>Develop a skill AI can't easily replicate</li>
        <li>Create something that demonstrates your unique value</li>
      </ul>

      <p className="mb-6">
        The 1:2 ratio ensures your anxiety converts to capability instead of just more anxiety.
      </p>

      <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">The Meta-Skill: Adaptability Over Prediction</h2>

      <p className="mb-6">
        Here's what research on technological disruption shows: The people who thrive aren't the ones who correctly predicted the future—they're the ones who adapted quickly when it arrived.
      </p>

      <p className="mb-6">
        You don't need perfect forecasting. You need:
      </p>

      <ul className="list-disc pl-6 mb-6 space-y-2">
        <li>Awareness of broad trends (without obsessing over details)</li>
        <li>Diverse skill development (not hyper-specialization)</li>
        <li>Comfort with uncertainty (not elimination of it)</li>
        <li>Bias toward action (not endless analysis)</li>
      </ul>

      <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">The Bottom Line</h2>

      <p className="mb-6">
        AI uncertainty triggers future-oriented analysis without endpoints—your brain keeps trying to solve an unsolvable problem.
      </p>

      <p className="mb-6">
        The solution isn't to ignore AI developments or suppress anxiety. It's to structure your future-thinking with boundaries, action requirements, and acceptance of unknowables.
      </p>

      <p className="mb-6">
        You can be appropriately prepared without being perpetually anxious.
      </p>

      <p className="mb-6">
        The future is uncertain—that's not new. What's new is having tools to think about uncertainty productively instead of spinning in it endlessly.
      </p>

      <p className="mb-6">
        Stop trying to predict the unpredictable. Start building adaptability, taking action, and living in the present while preparing for multiple futures.
      </p>

      <p className="mb-6">
        The most future-proof skill isn't knowing what's coming. It's being able to adapt to whatever arrives.
      </p>
    </BlogArticleTemplate>
  );
}
